// Groupchat Settings Beta Modal (PRD §1.7, WP-04).
import { useId, useRef, useState } from 'react';
import { useI18n } from '../../lib/i18n';
import { useDialogA11y } from '../../lib/useDialogA11y';
import { CloseIcon, CheckIcon } from '../icons';

interface GroupChatBetaModalProps {
  onClose: () => void;
}

export default function GroupChatBetaModal({ onClose }: GroupChatBetaModalProps) {
  const { t } = useI18n();
  const [groupName, setGroupName] = useState('');
  const [enabled, setEnabled] = useState(false);
  const [position, setPosition] = useState<'bottom-right' | 'bottom-left'>('bottom-right');
  const [saved, setSaved] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  useDialogA11y(true, panelRef, onClose);

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();
    setSaved(true);
    setTimeout(() => {
      onClose();
    }, 800);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4 backdrop-blur-sm">
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="w-full max-w-md overflow-hidden rounded-2xl border border-line bg-white shadow-2xl dark:border-slate-800 dark:bg-slate-900"
      >
        <div className="flex items-center justify-between border-b border-line px-5 py-4 dark:border-slate-800">
          <div>
            <div className="flex items-center gap-2">
              <h3 id={titleId} className="text-sm font-bold text-ink dark:text-slate-100">
                {t('inbox.groupchat.title') || 'Groupchat Settings'}
              </h3>
              <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold text-amber-700 dark:bg-amber-950/40 dark:text-amber-400">
                BETA
              </span>
            </div>
            <p className="text-xs text-ink-3 dark:text-slate-400">
              {t('inbox.groupchat.subtitle') || 'Configure multichannel widget groupchat (experimental)'}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('common.close') || 'Close'}
            className="grid h-8 w-8 place-items-center rounded-lg text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"
          >
            <CloseIcon size={16} />
          </button>
        </div>

        <form onSubmit={handleSave} className="p-5 space-y-4">
          <div>
            <label className="block text-xs font-bold text-ink mb-1 dark:text-slate-200">
              {t('inbox.groupchat.groupName') || 'Group Name'}
            </label>
            <input
              type="text"
              value={groupName}
              onChange={(e) => setGroupName(e.target.value)}
              placeholder={t('inbox.groupChatTitlePlaceholder') || 'e.g. VIP Customer Support'}
              className="w-full rounded-xl border border-line bg-surface px-3 py-2 text-xs text-ink outline-none focus:border-brand dark:border-slate-800 dark:bg-slate-800 dark:text-slate-200"
            />
          </div>

          <div>
            <label className="block text-xs font-bold text-ink mb-1 dark:text-slate-200">
              {t('inbox.groupchat.position') || 'Widget Position'}
            </label>
            <select
              value={position}
              onChange={(e) => setPosition(e.target.value as any)}
              className="w-full rounded-xl border border-line bg-surface px-3 py-2 text-xs text-ink outline-none focus:border-brand dark:border-slate-800 dark:bg-slate-800 dark:text-slate-200"
            >
              <option value="bottom-right">Bottom Right</option>
              <option value="bottom-left">Bottom Left</option>
            </select>
          </div>

          <div className="flex items-center justify-between border-t border-line pt-3 dark:border-slate-800">
            <span className="text-xs font-bold text-ink dark:text-slate-200">
              {t('inbox.groupchat.enable') || 'Enable Groupchat Widget'}
            </span>
            <input
              type="checkbox"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
              className="h-4 w-4 rounded text-brand focus:ring-brand"
            />
          </div>

          <div className="flex items-center justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-xl border border-line px-4 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50 dark:border-slate-800 dark:text-slate-300 dark:hover:bg-slate-800"
            >
              {t('common.cancel') || 'Cancel'}
            </button>
            <button
              type="submit"
              className="flex items-center gap-1 rounded-xl bg-brand px-5 py-2 text-xs font-bold text-white shadow-sm hover:bg-brand-dark"
            >
              {saved ? <CheckIcon size={14} /> : null}
              <span>{saved ? t('common.saved') || 'Saved!' : t('common.save') || 'Save'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
